/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CallFlowsPage.tsx
 ??? Created: 2025-01-28
 ?? Description:
    Call Flows list page with table view, search, pagination, and actions.
    Allows viewing, creating, duplicating, editing, deleting, and toggling call flows.

 ?? Notes:
    - Domain-scoped (multi-tenant safe)
    - Supports search and pagination
    - Shows name, extension, status, destinations, and enabled flag

 ?? Last Updated: 2025-11-07 by GPT-5 Codex
----------------------------------------------------------------*/

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Edit, GitBranch, Power, Trash2 } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import DataGridSimple from '../../components/ui/DataGridSimple';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigationStore } from '../../store/navigation';
import { deleteCallFlowById, navigateToCallFlowDuplicate } from './callFlowsActions';
import { useCallFlowList } from './hooks/useCallFlowList';
import { toggleCallFlow } from './api/callFlowApi';
import { CallFlow } from './types';

const ACTION_BUTTON_CLASSNAMES = {
  base: 'p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60',
  edit: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
  duplicate: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  delete: 'hover:bg-red-100 dark:hover:bg-red-900/30',
  toggle: 'hover:bg-slate-100 dark:hover:bg-slate-800',
};

export default function CallFlowsPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const pageTitle = useMemo(() => t('callFlows.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; callFlow: CallFlow | null }>(
    { isOpen: false, callFlow: null },
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useCallFlowList();

  const handleCreate = () => {
    navigate('/pbx/incoming-tools/call-flows/new');
  };

  const handleEdit = (callFlow: CallFlow) => {
    navigate(`/pbx/incoming-tools/call-flows/${callFlow.uuid ?? callFlow.id}`);
  };

  const handleDuplicate = (callFlow: CallFlow) => {
    navigateToCallFlowDuplicate(navigate, callFlow.uuid ?? callFlow.id);
  };

  const handleDeleteClick = useCallback((callFlow: CallFlow) => {
    setDeleteModal({ isOpen: true, callFlow });
  }, []);

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'name') {
          return {
            ...column,
            render: (_value: unknown, item: CallFlow) => (
              <span className="font-medium text-foreground">{item.name}</span>
            ),
          };
        }
        if (column.key === 'extension') {
          return {
            ...column,
            render: (_value: unknown, item: CallFlow) => (
              <span className="text-sm text-muted-foreground">
                {item.extension || t('common:notAvailable')}
              </span>
            ),
          };
        }
        if (column.key === 'status') {
          return {
            ...column,
            render: (_value: unknown, item: CallFlow) => (
              <span
                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                  item.status
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300'
                }`}
              >
                {item.status ? t('callFlows.status.enabled') : t('callFlows.status.disabled')}
              </span>
            ),
          };
        }
        if (column.key === 'destinations') {
          return {
            ...column,
            render: (_value: unknown, item: CallFlow) => {
              // Helper function to translate destination
              const translateDestination = (module: string | undefined, value: string | undefined) => {
                if (!module) return null;
                
                // Translate module name
                const moduleLabel = t(`outboundRoutes.modules.${module}.label`, { defaultValue: module });
                
                // For terminate_call, translate the cause code
                if (module === 'terminate_call' && value) {
                  const causeLabel = t(`outboundRoutes.terminateCauses.${value}.label`, { defaultValue: value });
                  return `${moduleLabel} � ${causeLabel}`;
                }
                
                // For other modules, show the value as-is
                return value ? `${moduleLabel} � ${value}` : moduleLabel;
              };

              const enabledDestination = translateDestination(item.enabledDestinationModule, item.enabledDestinationData);
              const disabledDestination = translateDestination(item.disabledDestinationModule, item.disabledDestinationData);

              return (
                <div className="text-sm">
                  <div className="font-medium text-foreground">
                    {t('callFlows.destinations.enabled')}: {enabledDestination || t('common:notAvailable')}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t('callFlows.destinations.disabled')}: {disabledDestination || t('common:notAvailable')}
                  </div>
                </div>
              );
            },
          };
        }
        if (column.key === 'enabled') {
          return {
            ...column,
            render: (_value: unknown, item: CallFlow) => (
              <span
                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                  item.enabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
                }`}
              >
                {item.enabled ? t('common:enabled') : t('common:disabled')}
              </span>
            ),
          };
        }
        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: CallFlow) => {
              const date = new Date(item.createdAt);
              const isValid = !Number.isNaN(date.getTime());
              return (
                <span className="text-sm text-muted-foreground">
                  {isValid ? date.toLocaleDateString() : t('common:notAvailable')}
                </span>
              );
            },
          };
        }
        return column;
      }),
    [baseColumns, t],
  );

  const handleToggleStatus = useCallback(
    async (callFlow: CallFlow) => {
      try {
        await toggleCallFlow(callFlow.id, { status: !callFlow.status });

        showSuccess(
          t('callFlows.messages.toggleSuccess', {
            name: callFlow.name,
            status: !callFlow.status ? t('callFlows.status.enabled') : t('callFlows.status.disabled'),
          }),
        );
        refresh();
      } catch (error) {
        showError(error.message || t('callFlows.messages.toggleError'));
      }
    },
    [refresh, showError, showSuccess, t],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.callFlow) return;

    setIsDeleting(true);
    try {
      await deleteCallFlowById(deleteModal.callFlow.id);
      showSuccess(t('callFlows.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, callFlow: null });
      refresh();
    } catch (error) {
      showError(error.message || t('callFlows.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.callFlow, refresh, showError, showSuccess, t]);

  const renderActions = useCallback(
    (callFlow: CallFlow) => (
      <div className="flex items-center gap-2">
        {callFlow.toggleEnabled && (
          <SimpleTooltip content={t('callFlows.actions.toggle')} position="top">
            <button
              type="button"
              onClick={() => handleToggleStatus(callFlow)}
              aria-label={t('callFlows.actions.toggle')}
              className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.toggle}`}
            >
              <Power
                className={`h-4 w-4 ${callFlow.status ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}`}
              />
            </button>
          </SimpleTooltip>
        )}
        <SimpleTooltip content={t('common:edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(callFlow)}
            aria-label={t('common:edit')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.edit}`}
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(callFlow)}
            aria-label={t('common:duplicate')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.duplicate}`}
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('common:delete')} position="top">
          <button
            type="button"
            onClick={() => handleDeleteClick(callFlow)}
            aria-label={t('common:delete')}
            className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.delete}`}
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, handleToggleStatus, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <>
          <DataGridSimple
            data={data}
            columns={columns.map((column) => ({
              ...column,
              render:
                column.key === 'actions'
                  ? (_value, item: CallFlow) => renderActions(item)
                  : column.render,
            }))}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            onAdd={handleCreate}
            title={pageTitle}
            titleIcon={<GitBranch className="h-5 w-5 text-primary" />}
            addButtonText={t('callFlows.actions.create')}
            searchPlaceholder={t('callFlows.searchPlaceholder')}
            emptyMessage={t('callFlows.noResults')}
          />

          <ConfirmationModal
            isOpen={deleteModal.isOpen}
            onClose={() => setDeleteModal({ isOpen: false, callFlow: null })}
            onConfirm={handleDeleteConfirm}
            title={t('callFlows.messages.deleteTitle')}
            message={t('callFlows.messages.confirmDelete', {
              name: deleteModal.callFlow?.name ?? '',
            })}
            confirmText={t('common:delete')}
            cancelText={t('common:cancel')}
            type="danger"
            isLoading={isDeleting}
          />
        </>
      }
      formView={null}
    />
  );
}

